import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useNavigate, useParams } from '@tanstack/react-router';
import { Info } from 'lucide-react';
import { Button, Card, cn, ErrorState, Field, LoadingState } from '@pixlide/ui';
import {
  useProject,
  useProjectPreview,
  useTemplateDetail,
  useUpdateProjectContent,
  type ProjectFieldInput,
} from '@/api/queries/projects';
import { useUploadMediaAsset } from '@/api/queries/media';
import { frameClassForFormat } from '@/lib/video-format';
import { toRenderableImage, UnsupportedImageError } from '@/lib/renderable-image';

const PREVIEW_DEBOUNCE_MS = 500;

/** Même limite que `VideoProject.MaxNameLength` côté domaine, qui refuserait au-delà. */
const PROJECT_NAME_MAX_LENGTH = 150;

// `mediaAssetId` est typé `string | undefined` et NON optionnel : avec
// `exactOptionalPropertyTypes`, un champ optionnel (`mediaAssetId?: string`) refuse qu'on lui
// assigne explicitement `undefined`, ce que la mise à jour de l'état fait naturellement ici.
type FieldState = Record<string, { value: string; mediaAssetId: string | undefined }>;

/** Bloc d'option du panneau « Options de la vidéo » : titre, aide, et contrôle à droite ou dessous. */
function OptionBlock({
  title,
  hint,
  aside,
  children,
}: {
  title: string;
  hint?: string;
  aside?: ReactNode;
  children?: ReactNode;
}) {
  return (
    <section className="flex flex-col gap-3 rounded-2xl bg-white/90 p-4 ring-1 ring-ink-100">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold">{title}</h3>
          {hint ? <p className="mt-0.5 text-xs text-ink-500">{hint}</p> : null}
        </div>
        {aside}
      </div>
      {children}
    </section>
  );
}

function ValuePill({ children }: { children: ReactNode }) {
  return (
    <span className="rounded-pill bg-white px-3 py-1 text-xs font-semibold shadow-card ring-1 ring-ink-100">
      {children}
    </span>
  );
}

/**
 * Formulaire généré depuis le schéma du template (`TemplateFieldResponse.Kind`), avec un
 * aperçu image fixe débouncé. Premier composant de ce type dans l'application : il n'existe
 * pas encore de bibliothèque de « formulaire dynamique depuis un schéma » à réutiliser.
 */
export function ProjectEditorPage() {
  const { projectId } = useParams({ from: '/app/creer/template/$projectId' });
  const navigate = useNavigate();

  const project = useProject(projectId);
  const templateId = project.data?.summary.templateId ?? null;
  const template = useTemplateDetail(templateId);
  const updateContent = useUpdateProjectContent(projectId);
  const preview = useProjectPreview();
  const uploadMedia = useUploadMediaAsset();

  const [fields, setFields] = useState<FieldState>({});
  const [name, setName] = useState('');
  const [initialized, setInitialized] = useState(false);
  const [imageErrors, setImageErrors] = useState<Record<string, string | undefined>>({});

  // Pré-remplissage à partir des valeurs déjà enregistrées sur le projet (reprise d'un
  // brouillon) — une seule fois, pour ne pas écraser la saisie en cours à chaque refetch.
  useEffect(() => {
    if (initialized || !project.data) {
      return;
    }

    const next: FieldState = {};

    for (const field of project.data.fields) {
      next[field.key] = { value: field.value ?? '', mediaAssetId: field.mediaAssetId ?? undefined };
    }

    setFields(next);
    setName(project.data.summary.name);
    setInitialized(true);
  }, [initialized, project.data]);

  // Limité aux champs que le template propose AUJOURD'HUI : un projet créé avant qu'un champ
  // ne soit figé en back-office conserve sa valeur en base, mais la renvoyer ferait échouer
  // l'enregistrement — le serveur traite un champ figé comme une clé inconnue.
  const editableKeys = useMemo(
    () => new Set((template.data?.fields ?? []).map((field) => field.key)),
    [template.data],
  );

  const fieldInputs = useMemo<ProjectFieldInput[]>(
    () =>
      Object.entries(fields)
        .filter(([key]) => editableKeys.has(key))
        .filter(([, state]) => state.value !== '' || state.mediaAssetId !== undefined)
        .map(([key, state]) => ({
          key,
          value: state.value === '' ? null : state.value,
          mediaAssetId: state.mediaAssetId ?? null,
        })),
    [fields, editableKeys],
  );

  // Aperçu débouncé : recalculé après une pause de saisie, jamais à chaque frappe — chaque
  // appel déclenche un vrai rendu Skottie côté serveur (voir la limite de débit dédiée).
  useEffect(() => {
    if (!templateId || fieldInputs.length === 0) {
      return;
    }

    const timeout = setTimeout(() => {
      preview.mutate({ templateId, fields: fieldInputs });
      // eslint-disable-next-line react-hooks/exhaustive-deps -- `preview` est stable (useMutation), on ne veut déclencher que sur un changement réel des champs.
    }, PREVIEW_DEBOUNCE_MS);

    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templateId, fieldInputs]);

  if (project.isPending || template.isPending) {
    return <LoadingState label="Chargement du projet…" />;
  }

  if (project.isError || !project.data) {
    return <ErrorState message="Impossible de charger ce projet." onRetry={() => void project.refetch()} />;
  }

  if (template.isError || !template.data) {
    return <ErrorState message="Impossible de charger le template." onRetry={() => void template.refetch()} />;
  }

  const setTextValue = (key: string, value: string) => {
    setFields((current) => ({ ...current, [key]: { value, mediaAssetId: current[key]?.mediaAssetId } }));
  };

  const onImageChange = async (key: string, file: File | null) => {
    if (!file) {
      return;
    }

    setImageErrors((current) => ({ ...current, [key]: undefined }));

    try {
      // AVIF converti dans le navigateur : le moteur de rendu ne sait pas le décoder.
      const renderable = await toRenderableImage(file);
      const mediaAssetId = await uploadMedia.mutateAsync(renderable);
      setFields((current) => ({ ...current, [key]: { value: current[key]?.value ?? '', mediaAssetId } }));
    } catch (error) {
      const message =
        error instanceof UnsupportedImageError ? error.message : "Le dépôt de l'image a échoué. Réessayez.";
      setImageErrors((current) => ({ ...current, [key]: message }));
    }
  };

  const trimmedName = name.trim();

  const onSubmit = async () => {
    // Nom détouré : un nom avec des espaces de bord serait refusé par le domaine.
    try {
      await updateContent.mutateAsync({ fields: fieldInputs, submitForRender: true, name: trimmedName });
    } catch {
      return; // Message rendu depuis `updateContent.isError`.
    }

    await navigate({ to: '/videos' });
  };

  return (
    <div className="mx-auto flex max-w-6xl flex-col gap-8">
      <div>
        <h1 className="text-3xl font-semibold tracking-tight">Configuration du template</h1>
        <p className="mt-1.5 text-ink-500">Personnalisez le contenu de votre présentation vidéo.</p>
      </div>

      <div className="grid items-start gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)]">
        <Card className="flex flex-col gap-4 p-5">
          <h2 className="flex items-center gap-2.5 text-base font-semibold">
            <span aria-hidden className="grid size-7 place-items-center rounded-full bg-brand-100 text-brand-500">
              <Info className="size-4" />
            </span>
            Options de la vidéo
          </h2>

          <OptionBlock title="Nommer votre vidéo" hint="C'est sous ce nom que vous la retrouverez dans « Mes vidéos ».">
            <Field
              label="Nom de la vidéo"
              required
              value={name}
              maxLength={PROJECT_NAME_MAX_LENGTH}
              onChange={(event) => setName(event.target.value)}
              placeholder="Menu du midi — semaine 12"
            />
          </OptionBlock>

          {template.data.fields.length > 0 ? (
            <OptionBlock title="Contenu" hint="Les textes et images affichés dans la vidéo.">
          {template.data.fields.map((field) => {
            const state = fields[field.key] ?? { value: '', mediaAssetId: undefined };

            switch (field.kind) {
              case 1: // LongText
                return (
                  <div key={field.key} className="flex flex-col gap-1.5">
                    <label className="text-[11px] leading-4 font-medium text-ink-300">{field.label}</label>
                    <textarea
                      value={state.value}
                      maxLength={field.maxLength ?? undefined}
                      onChange={(event) => setTextValue(field.key, event.target.value)}
                      className="min-h-24 rounded-xl bg-white px-3.5 py-2.5 text-sm ring-1 ring-ink-100 focus:outline-none focus:ring-2 focus:ring-ink-900"
                    />
                  </div>
                );

              case 2: // Price
                return (
                  <Field
                    key={field.key}
                    label={field.label}
                    type="text"
                    inputMode="decimal"
                    value={state.value}
                    required={field.isRequired}
                    onChange={(event) => setTextValue(field.key, event.target.value)}
                  />
                );

              case 3: // Image
                return (
                  <div key={field.key} className="flex flex-col gap-1.5">
                    <label className="text-[11px] leading-4 font-medium text-ink-300">{field.label}</label>
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/webp,image/avif"
                      disabled={uploadMedia.isPending}
                      onChange={(event) => void onImageChange(field.key, event.target.files?.[0] ?? null)}
                      className="text-sm text-ink-500"
                    />
                    {imageErrors[field.key] ? (
                      <p role="alert" className="text-xs text-danger-500">
                        {imageErrors[field.key]}
                      </p>
                    ) : state.mediaAssetId ? (
                      <p className="text-xs text-success-500">Image déposée</p>
                    ) : null}
                  </div>
                );

              case 5: // Color
                return (
                  <div key={field.key} className="flex flex-col gap-1.5">
                    <label className="text-[11px] leading-4 font-medium text-ink-300">{field.label}</label>
                    <input
                      type="color"
                      value={state.value || '#000000'}
                      onChange={(event) => setTextValue(field.key, event.target.value)}
                      className="h-9 w-16 rounded-md"
                    />
                  </div>
                );

              case 4: // Video — pas de substitution dans ce premier lot
                return null;

              default: // Text
                return (
                  <Field
                    key={field.key}
                    label={field.label}
                    value={state.value}
                    maxLength={field.maxLength ?? undefined}
                    required={field.isRequired}
                    onChange={(event) => setTextValue(field.key, event.target.value)}
                  />
                );
            }
          })}

            </OptionBlock>
          ) : null}

          <OptionBlock
            title="Durée de la boucle"
            hint={`Optimisé pour ${template.data.durationSeconds} secondes`}
            aside={<ValuePill>{template.data.durationSeconds}s</ValuePill>}
          />

          <OptionBlock title="Format d'export" hint="Format mp4 conseillé" aside={<ValuePill>mp4</ValuePill>} />

          {updateContent.isError ? (
            <p role="alert" className="text-sm text-danger-500">
              Impossible d'enregistrer la vidéo. Vérifiez les champs puis réessayez.
            </p>
          ) : null}

          <Button
            isLoading={updateContent.isPending}
            disabled={trimmedName === ''}
            onClick={() => void onSubmit()}
            className="self-end"
          >
            Enregistrer et lancer le rendu
          </Button>
        </Card>

        <div className="flex flex-col gap-4">
          <Card className="overflow-hidden p-2">
            <div
              className={cn(
                'mx-auto flex w-full items-center justify-center overflow-hidden rounded-xl bg-ink-50 text-xs text-ink-300',
                frameClassForFormat(template.data.format),
                template.data.format === 1 && 'max-w-xs',
              )}
            >
              {preview.data ? (
                <img src={preview.data.imageDataUri} alt="Aperçu de la vidéo personnalisée" className="size-full object-cover" />
              ) : (
                <span>{preview.isPending ? 'Calcul…' : 'Modifiez un champ pour voir un aperçu.'}</span>
              )}
            </div>
          </Card>

          <div className="rounded-card bg-white/60 px-5 py-4 text-center ring-1 ring-ink-100">
            <p className="text-[11px] font-semibold tracking-wide text-brand-500 uppercase">Conseil d'expert</p>
            <p className="mt-1 text-sm text-ink-500">
              L'aperçu se met à jour quelques instants après chaque modification : relisez-le avant de
              lancer le rendu.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
